import React, { useState, useEffect, useCallback } from 'react';
import { sessions } from '../api';
import { Clock, DollarSign, Bot, RefreshCw, Loader2, Calendar, TrendingUp, BarChart3 } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import DateRangePicker from './DateRangePicker';

const Usage = () => {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('week'); // 'day', 'week', or 'month'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);

  const fetchUsageData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = useCustomRange && customStartDate && customEndDate
        ? await sessions.usage(null, customStartDate, customEndDate)
        : await sessions.usage(period);
      setUsageData(response.data);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [period, customStartDate, customEndDate, useCustomRange]);

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  const handleDateRangeChange = (startDate, endDate) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    const hasCustomRange = !!(startDate && endDate);
    setUseCustomRange(hasCustomRange);
    if (hasCustomRange) {
      setPeriod('custom');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPeriodLabel = () => {
    if (useCustomRange && customStartDate && customEndDate) {
      return 'Custom Range';
    }
    switch (period) {
      case 'day':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'custom':
        return 'Custom Range';
      default:
        return period;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading usage data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Usage</h1>
          <p className="text-sm text-muted-foreground">Agent usage and cost tracking</p>
        </div>
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6 text-center">
          <div className="flex items-center justify-center gap-2 text-destructive mb-4">
            <span>{error}</span>
          </div>
          <button
            onClick={fetchUsageData}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Usage</h1>
          <p className="text-sm text-muted-foreground">Agent usage and cost tracking</p>
        </div>
        <button
          onClick={fetchUsageData}
          className="px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Period Selector */}
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-primary" />
          <span className="font-medium text-foreground">Time Period</span>
        </div>
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            {['day', 'week', 'month'].map((p) => (
              <button
                key={p}
                onClick={() => {
                  setPeriod(p);
                  setUseCustomRange(false);
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  period === p && !useCustomRange
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-foreground hover:bg-muted'
                }`}
              >
                {p === 'day' ? 'Today' : p === 'week' ? 'This Week' : 'This Month'}
              </button>
            ))}
          </div>
          <div className="pt-2 border-t border-border">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Custom Range:</span>
              <div className="flex-1 max-w-md">
                <DateRangePicker
                  startDate={customStartDate}
                  endDate={customEndDate}
                  onChange={handleDateRangeChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {usageData && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Total Minutes */}
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {usageData.total_minutes.toFixed(2)}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Minutes</div>
                </div>
              </div>
            </div>

            {/* Total Cost */}
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    ${usageData.total_cost_usd.toFixed(4)}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Cost</div>
                </div>
              </div>
            </div>

            {/* Agent Count */}
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center">
                  <Bot className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {usageData.agents.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Active Agents</div>
                </div>
              </div>
            </div>
          </div>

          {/* Period Info */}
          <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{getPeriodLabel()}:</span>{' '}
              {formatDate(usageData.start_date)} - {formatDate(usageData.end_date)}
            </div>
          </div>

          {/* Agent Usage Table */}
          <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Usage by Agent</h2>
              </div>
            </div>
            
            {usageData.agents.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No usage data available for {getPeriodLabel().toLowerCase()}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-4 text-sm font-medium text-foreground">Agent</th>
                      <th className="text-right p-4 text-sm font-medium text-foreground">Minutes</th>
                      <th className="text-right p-4 text-sm font-medium text-foreground">Cost</th>
                      <th className="text-right p-4 text-sm font-medium text-foreground">Sessions</th>
                      <th className="text-right p-4 text-sm font-medium text-foreground">Avg Cost/Min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageData.agents.map((agent, index) => {
                      const avgCostPerMin = agent.total_minutes > 0 
                        ? agent.total_cost_usd / agent.total_minutes 
                        : 0;
                      
                      return (
                        <tr
                          key={agent.agent_id}
                          className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${
                            index % 2 === 0 ? 'bg-card/30' : ''
                          }`}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Bot className="w-4 h-4 text-primary" />
                              <span className="font-medium text-foreground">{agent.agent_name}</span>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="text-foreground font-medium">
                                {agent.total_minutes.toFixed(2)}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <DollarSign className="w-4 h-4 text-muted-foreground" />
                              <span className="text-foreground font-medium">
                                ${agent.total_cost_usd.toFixed(4)}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-foreground">{agent.session_count}</span>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <TrendingUp className="w-4 h-4 text-muted-foreground" />
                              <span className="text-foreground">
                                ${avgCostPerMin.toFixed(4)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Usage;

